/**
 * "Catch the Treats" — original PRENDFORM minigame (not a copy of any
 * reference game). Vanilla JS + CSS only, driven by requestAnimationFrame.
 * DOM-based (no canvas/WebGL), lightweight on purpose.
 *
 * Rules: move the bowl horizontally (mouse / touch drag / arrow keys) to
 * catch falling treats. Reach GOAL catches within TIME_LIMIT_MS to win.
 * No obstacles, no lives lost on a miss — the point is to entertain while
 * the preview generates, not to frustrate.
 *
 * On a win, claimGameReward() is called: for now it only marks the win
 * locally in the shared funnel state (via funnel-state.js). It is the single
 * place a real backend validation (session, score, reward/discount code)
 * must be wired into later — no discount is actually issued here.
 */
window.PfCatchTheTreats = (() => {
  const GOAL = 10;
  const TIME_LIMIT_MS = 15000;
  const SPAWN_INTERVAL_MS = 750;
  const TREAT_EMOJIS = ['🦴', '🍪', '🐾'];

  function init(root) {
    const intro = root.querySelector('[data-game-intro]');
    const playArea = root.querySelector('[data-game-play-area]');
    const bowl = root.querySelector('[data-game-bowl]');
    const scoreEl = root.querySelector('[data-game-score]');
    const timerEl = root.querySelector('[data-game-timer]');
    const hud = root.querySelector('[data-game-hud]');
    const winScreen = root.querySelector('[data-game-win]');
    const loseScreen = root.querySelector('[data-game-lose]');
    const rewardLabelEls = root.querySelectorAll('[data-game-reward-label]');
    const playButton = root.querySelector('[data-game-play]');
    const retryButtons = root.querySelectorAll('[data-game-retry]');

    // Win screen text swaps on a repeat win (reward already unlocked
    // earlier this session) — see endGame(). Defaults captured once so the
    // very first win always shows the section's own configured copy.
    const winTitleEl = root.querySelector('[data-game-win-title]');
    const winSubtitleEl = root.querySelector('[data-game-win-subtitle]');
    if (winTitleEl) winTitleEl.dataset.defaultText = winTitleEl.textContent;
    if (winSubtitleEl) winSubtitleEl.dataset.defaultText = winSubtitleEl.textContent;
    const winRepeatTitle = (winTitleEl && winTitleEl.dataset.repeatText) || winTitleEl?.dataset.defaultText;
    const winRepeatSubtitle = (winSubtitleEl && winSubtitleEl.dataset.repeatText) || winSubtitleEl?.dataset.defaultText;

    const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const reward = window.PfRewardConfig || {};

    rewardLabelEls.forEach((el) => {
      el.textContent = reward.rewardLabel || '';
    });

    let score = 0;
    let bowlX = 0.5; // 0..1 fraction of play area width
    let treats = [];
    let spawnTimer = null;
    let countdownTimer = null;
    let rafId = null;
    let running = false;
    let timeLeftMs = TIME_LIMIT_MS;
    let lastFrameTime = 0;
    let pointerActive = false;

    function setScreen(name) {
      intro.hidden = name !== 'intro';
      playArea.hidden = name !== 'playing';
      hud.hidden = name !== 'playing';
      winScreen.hidden = name !== 'win';
      loseScreen.hidden = name !== 'lose';
    }

    function updateBowlPosition() {
      const areaWidth = playArea.clientWidth;
      const bowlWidth = bowl.offsetWidth;
      const clampedX = Math.min(1, Math.max(0, bowlX));
      const left = clampedX * (areaWidth - bowlWidth);
      bowl.style.left = `${left}px`;
    }

    function setBowlFromClientX(clientX) {
      const rect = playArea.getBoundingClientRect();
      bowlX = (clientX - rect.left) / rect.width;
      updateBowlPosition();
    }

    function spawnTreat() {
      const treat = document.createElement('div');
      treat.className = 'game-treat';
      treat.textContent = TREAT_EMOJIS[Math.floor(Math.random() * TREAT_EMOJIS.length)];
      const startXPercent = 8 + Math.random() * 84; // keep inside bounds
      treat.style.left = `${startXPercent}%`;
      treat.dataset.top = '-40';
      treat.style.top = '-40px';
      playArea.appendChild(treat);
      treats.push({
        el: treat,
        top: -40,
        speed: 60 + Math.random() * 40, // px/second
      });
    }

    function endGame(won) {
      running = false;
      clearInterval(spawnTimer);
      clearInterval(countdownTimer);
      cancelAnimationFrame(rafId);
      treats.forEach((t) => t.el.remove());
      treats = [];

      const gameState = {
        gamePlayed: true,
        gameScore: score,
        gameWon: won,
      };

      if (won) {
        // Reward is capped at one per session — a repeat win never stacks
        // a second discount, it just confirms the one already unlocked.
        const alreadyUnlocked = !!(window.PfFunnel && window.PfFunnel.get().discountReward);
        if (!alreadyUnlocked) {
          gameState.discountReward = {
            rewardType: reward.rewardType,
            rewardValue: reward.rewardValue,
            rewardLabel: reward.rewardLabel,
          };
          gameState.rewardClaimed = false;
          claimGameReward(gameState.discountReward, score);
        }
        setScreen('win');
        if (winTitleEl) winTitleEl.textContent = alreadyUnlocked ? winRepeatTitle : winTitleEl.dataset.defaultText;
        if (winSubtitleEl) winSubtitleEl.textContent = alreadyUnlocked ? winRepeatSubtitle : winSubtitleEl.dataset.defaultText;
      } else {
        setScreen('lose');
      }

      window.PfFunnel?.set(gameState);
    }

    function tick(now) {
      if (!running) return;
      if (!lastFrameTime) lastFrameTime = now;
      const deltaSeconds = (now - lastFrameTime) / 1000;
      lastFrameTime = now;

      const areaHeight = playArea.clientHeight;
      const bowlRect = bowl.getBoundingClientRect();
      const areaRect = playArea.getBoundingClientRect();

      treats = treats.filter((treat) => {
        treat.top += treat.speed * deltaSeconds;
        treat.el.style.top = `${treat.top}px`;

        const treatRect = treat.el.getBoundingClientRect();
        const caughtByY = treatRect.bottom >= bowlRect.top && treatRect.top <= bowlRect.bottom;
        const caughtByX = treatRect.right >= bowlRect.left && treatRect.left <= bowlRect.right;

        if (caughtByY && caughtByX) {
          catchTreat(treat);
          return false;
        }

        if (treat.top > areaHeight) {
          treat.el.remove();
          return false;
        }

        return true;
      });

      rafId = requestAnimationFrame(tick);
    }

    function catchTreat(treat) {
      score += 1;
      scoreEl.textContent = `${score} / ${GOAL}`;
      treat.el.classList.add('is-caught');
      setTimeout(() => treat.el.remove(), 180);

      if (score >= GOAL) {
        endGame(true);
      }
    }

    function startCountdown() {
      timeLeftMs = TIME_LIMIT_MS;
      timerEl.textContent = formatTime(timeLeftMs);
      countdownTimer = setInterval(() => {
        timeLeftMs -= 1000;
        timerEl.textContent = formatTime(Math.max(0, timeLeftMs));
        if (timeLeftMs <= 0) {
          endGame(false);
        }
      }, 1000);
    }

    function formatTime(ms) {
      const seconds = Math.ceil(ms / 1000);
      return `${String(seconds).padStart(2, '0')} s`;
    }

    function startGame() {
      score = 0;
      bowlX = 0.5;
      scoreEl.textContent = `0 / ${GOAL}`;
      setScreen('playing');
      updateBowlPosition();

      running = true;
      lastFrameTime = 0;
      spawnTimer = setInterval(spawnTreat, SPAWN_INTERVAL_MS);
      startCountdown();
      rafId = requestAnimationFrame(tick);

      window.PfFunnel?.set({ gameStarted: true });
    }

    // Input: mouse
    playArea.addEventListener('mousemove', (event) => {
      if (running) setBowlFromClientX(event.clientX);
    });

    // Input: touch drag — prevent page scroll only while actively dragging
    // inside the play area, never outside it.
    playArea.addEventListener(
      'touchstart',
      (event) => {
        pointerActive = true;
        if (running) setBowlFromClientX(event.touches[0].clientX);
      },
      { passive: true }
    );

    playArea.addEventListener(
      'touchmove',
      (event) => {
        if (!running || !pointerActive) return;
        event.preventDefault();
        setBowlFromClientX(event.touches[0].clientX);
      },
      { passive: false }
    );

    playArea.addEventListener('touchend', () => {
      pointerActive = false;
    });

    // Input: arrow keys
    playArea.setAttribute('tabindex', '0');
    playArea.addEventListener('keydown', (event) => {
      if (!running) return;
      const step = 0.06;
      if (event.key === 'ArrowLeft') {
        bowlX = Math.max(0, bowlX - step);
        updateBowlPosition();
        event.preventDefault();
      } else if (event.key === 'ArrowRight') {
        bowlX = Math.min(1, bowlX + step);
        updateBowlPosition();
        event.preventDefault();
      }
    });

    window.addEventListener('resize', () => {
      if (!playArea.hidden) updateBowlPosition();
    });

    playButton?.addEventListener('click', startGame);
    retryButtons.forEach((btn) => btn.addEventListener('click', startGame));

    setScreen('intro');
  }

  /**
   * Stub for the real reward validation. The minigame result (score, win)
   * is only ever provisional client-side state. A real backend must later
   * verify the session/score and issue any actual discount/code — nothing
   * here should be treated as a valid discount by itself.
   */
  function claimGameReward(reward, score) {
    console.info('[PfCatchTheTreats] claimGameReward() stub — no backend connected yet.', { reward, score });
    // TODO(backend): send { score, sessionInfo } to a real endpoint and
    // store the *validated* reward/token in the funnel state instead of
    // trusting this client-side result.
  }

  document.querySelectorAll('[data-catch-the-treats]').forEach(init);

  return { claimGameReward };
})();
